import { create } from 'zustand';
import { apiFetchAuthed } from '../apiHelpers';
import { DEFAULT_CONFIG, getOrderedModules } from '../Dashboard/moduleRegistry';

/** Deep-clone a config object (shallow clone of each module entry) */
const cloneConfig = (cfg) => ({
  ...cfg,
  modules: cfg.modules?.map(m => ({ ...m })) || [],
});

const useDashboardConfigStore = create((set, get) => ({
  config: null,         // Current working config (may have unsaved edits)
  savedConfig: null,    // Last-saved config (for cancel/revert)
  loading: true,
  editMode: false,

  fetchConfig: async () => {
    if (get()._fetching) return;
    set({ _fetching: true });
    try {
      const data = await apiFetchAuthed('/users/me', { silent: true });
      const config = data?.dashboardConfig || cloneConfig(DEFAULT_CONFIG);
      set({ config, savedConfig: cloneConfig(config), loading: false });
    } catch {
      const fallback = cloneConfig(DEFAULT_CONFIG);
      set({ config: fallback, savedConfig: cloneConfig(fallback), loading: false });
    } finally {
      set({ _fetching: false });
    }
  },

  saveConfig: async () => {
    const { config } = get();
    const normalized = {
      ...config,
      modules: getOrderedModules(config).map((m, i) => ({ ...m, order: i })),
    };
    try {
      await apiFetchAuthed('/users/me/dashboard', {
        method: 'PATCH',
        body: normalized,
      });
      set({ config: normalized, savedConfig: cloneConfig(normalized), editMode: false });
      return true;
    } catch {
      return false;
    }
  },

  setEditMode: (on) => {
    if (on) {
      set({ editMode: true, savedConfig: cloneConfig(get().config) });
    } else {
      set({ editMode: false, config: cloneConfig(get().savedConfig) });
    }
  },

  reorderModules: (fromIndex, toIndex) => {
    const { config } = get();
    const modules = [...getOrderedModules(config)]; // defensive copy before splice
    const [moved] = modules.splice(fromIndex, 1);
    modules.splice(toIndex, 0, moved);
    const reordered = modules.map((m, i) => ({ ...m, order: i }));
    set({ config: { ...config, modules: reordered } });
  },

  toggleModule: (moduleId) => {
    const { config } = get();
    const modules = getOrderedModules(config).map(m =>
      m.id === moduleId ? { ...m, visible: !m.visible } : m
    );
    set({ config: { ...config, modules } });
  },

  toggleSize: (moduleId) => {
    const { config } = get();
    const modules = getOrderedModules(config).map(m =>
      m.id === moduleId ? { ...m, size: m.size === 'full' ? 'half' : 'full' } : m
    );
    set({ config: { ...config, modules } });
  },

  resetToDefaults: () => {
    set({ config: cloneConfig(DEFAULT_CONFIG) });
  },
}));

export default useDashboardConfigStore;
